/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { PasswordDialogComponent } from '../users/password-dialog.component';
import { forkJoin } from 'rxjs';
import { AdminGroup, AdminService } from './admin.service';

@Component({
  selector: 'app-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatTooltipModule,
    MatIconModule,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
    <h2 mat-dialog-title class="aca-admin-dialog__title">
      <mat-icon>{{ isEdit ? 'manage_accounts' : 'person_add' }}</mat-icon>
      {{ isEdit ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้ใหม่' }}
    </h2>
    <div mat-dialog-content class="aca-admin-dialog__body">
      <mat-form-field *ngIf="!isEdit" appearance="outline" class="aca-admin-dialog__full">
        <mat-label>Username</mat-label>
        <input matInput [(ngModel)]="model.id" autocomplete="off" placeholder="เช่น jsmith" />
      </mat-form-field>

      <div class="aca-admin-dialog__row">
        <mat-form-field appearance="outline">
          <mat-label>ชื่อ</mat-label>
          <input matInput [(ngModel)]="model.firstName" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>นามสกุล</mat-label>
          <input matInput [(ngModel)]="model.lastName" />
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline" class="aca-admin-dialog__full">
        <mat-label>อีเมล</mat-label>
        <input matInput type="email" [(ngModel)]="model.email" />
      </mat-form-field>

      <div class="aca-admin-dialog__row">
        <mat-form-field appearance="outline">
          <mat-label>ตำแหน่ง</mat-label>
          <input matInput [(ngModel)]="model.jobTitle" placeholder="เช่น เจ้าหน้าที่เอกสาร" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>หน่วยงาน</mat-label>
          <input matInput [(ngModel)]="model.organization" placeholder="เช่น ฝ่ายบริหาร" />
        </mat-form-field>
      </div>

      <mat-form-field *ngIf="!isEdit" appearance="outline" class="aca-admin-dialog__full">
        <mat-label>รหัสผ่าน</mat-label>
        <input matInput type="password" [(ngModel)]="model.password" autocomplete="new-password" />
        <mat-hint>{{ 'DMS.PASSWORD.RULE' | translate: { min: minPasswordLength } }}</mat-hint>
      </mat-form-field>

      <ng-container *ngIf="!isEdit">
        <mat-form-field appearance="outline" class="aca-admin-dialog__full">
          <mat-label>เพิ่มเข้ากลุ่ม</mat-label>
          <input matInput [(ngModel)]="groupSearch" placeholder="พิมพ์เพื่อค้นหากลุ่ม…" autocomplete="off" />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <!-- Inline (non-overlay) results — simpler/robust positioning, no CDK overlay needed -->
        <div class="aca-admin-groups__results" *ngIf="groupSearch.trim()">
          <button type="button" class="aca-admin-groups__result" *ngFor="let g of filteredAvailable()" (click)="pickGroup(g.id)">
            <mat-icon>groups</mat-icon>
            <span>{{ g.displayName || g.id }}</span>
            <mat-icon class="aca-admin-groups__result-add">add</mat-icon>
          </button>
          <div class="aca-admin-groups__noresult" *ngIf="!filteredAvailable().length">ไม่พบกลุ่ม</div>
        </div>

        <div class="aca-admin-groups" *ngIf="model.groups.length">
          <h4 class="aca-admin-groups__heading">กลุ่มที่จะเพิ่ม ({{ model.groups.length }})</h4>
          <div class="aca-admin-groups__list">
            <span class="aca-admin-groups__chip" *ngFor="let g of selectedGroups()">
              <mat-icon>groups</mat-icon>
              {{ g.displayName || g.id }}
              <button class="aca-admin-groups__remove" type="button" (click)="removeGroup(g.id)" matTooltip="นำออก">
                <mat-icon>close</mat-icon>
              </button>
            </span>
          </div>
        </div>
      </ng-container>

      <mat-slide-toggle [(ngModel)]="model.enabled" color="primary">เปิดใช้งานบัญชี</mat-slide-toggle>

      <p class="aca-admin-dialog__error" *ngIf="error">{{ error }}</p>
    </div>
    <div mat-dialog-actions align="end" class="aca-admin-dialog__actions">
      <button mat-button (click)="close()">ยกเลิก</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || !valid()">
        {{ saving ? 'กำลังบันทึก…' : 'บันทึก' }}
      </button>
    </div>
  `
})
export class UserDialogComponent {
  private readonly admin = inject(AdminService);
  private readonly ref = inject(MatDialogRef<UserDialogComponent>);

  isEdit = false;
  saving = false;
  error = '';
  model: any = { id: '', firstName: '', lastName: '', email: '', password: '', jobTitle: '', organization: '', enabled: true, groups: [] };
  selectableGroups: AdminGroup[] = [];
  groupSearch = '';

  constructor() {
    const data = inject<any>(MAT_DIALOG_DATA);
    if (data?.person) {
      this.isEdit = true;
      this.model = {
        id: data.person.id,
        firstName: data.person.firstName || '',
        lastName: data.person.lastName || '',
        email: data.person.email || '',
        jobTitle: data.person.jobTitle || '',
        organization: data.person.company?.organization || '',
        enabled: data.person.enabled !== false,
        groups: []
      };
    } else {
      // Load groups for the picker (create mode only).
      this.admin.listGroups(0, 500).subscribe({
        next: (res) => {
          this.selectableGroups = (res?.list?.entries || []).map((e: any) => e.entry).filter((g: AdminGroup) => g.id !== 'GROUP_EVERYONE');
        }
      });
    }
  }

  valid(): boolean {
    if (this.isEdit) {
      return !!this.model.email;
    }
    return (
      !!this.model.id &&
      !!this.model.firstName &&
      !!this.model.lastName &&
      !!this.model.email &&
      (this.model.password ?? '').length >= this.minPasswordLength
    );
  }

  /** The same rule the change-password dialog holds an administrator to. */
  readonly minPasswordLength = PasswordDialogComponent.MIN_LENGTH;

  /** Groups not yet picked, filtered by the search text. */
  filteredAvailable(): AdminGroup[] {
    const picked = new Set<string>(this.model.groups || []);
    const q = (this.groupSearch || '').trim().toLowerCase();
    return this.selectableGroups.filter((g) => !picked.has(g.id) && (!q || (g.displayName || g.id).toLowerCase().includes(q))).slice(0, 50);
  }

  /** Picked group ids resolved to group objects (for the chip list). */
  selectedGroups(): AdminGroup[] {
    return (this.model.groups || []).map((id: string) => this.selectableGroups.find((g) => g.id === id) || { id });
  }

  pickGroup(id: string): void {
    if (id && !this.model.groups.includes(id)) {
      this.model.groups.push(id);
    }
    // clear the search so the inline result list collapses, ready for the next
    this.groupSearch = '';
  }

  removeGroup(id: string): void {
    this.model.groups = this.model.groups.filter((g: string) => g !== id);
  }

  save(): void {
    this.saving = true;
    this.error = '';
    if (this.isEdit) {
      this.admin
        .updatePerson(this.model.id, {
          firstName: this.model.firstName,
          lastName: this.model.lastName,
          email: this.model.email,
          jobTitle: this.model.jobTitle,
          company: { organization: this.model.organization },
          enabled: this.model.enabled
        })
        .subscribe({ next: () => this.ref.close(true), error: (e) => this.fail(e) });
      return;
    }

    this.admin
      .createPerson({
        id: this.model.id,
        firstName: this.model.firstName,
        lastName: this.model.lastName,
        email: this.model.email,
        password: this.model.password,
        jobTitle: this.model.jobTitle,
        company: { organization: this.model.organization },
        enabled: this.model.enabled
      })
      .subscribe({
        next: () => this.assignGroups(),
        error: (e) => this.fail(e)
      });
  }

  /** After creating the user, add them to the selected groups. */
  private assignGroups(): void {
    const groups: string[] = this.model.groups || [];
    if (!groups.length) {
      this.ref.close(true);
      return;
    }
    forkJoin(groups.map((groupId) => this.admin.addGroupMember(groupId, this.model.id))).subscribe({
      next: () => this.ref.close(true),
      // user was created; if a membership fails we still close (the user exists)
      error: () => this.ref.close(true)
    });
  }

  private fail(e: any): void {
    this.saving = false;
    this.error = e?.response?.body?.error?.briefSummary || e?.message || 'บันทึกไม่สำเร็จ';
  }

  close(): void {
    this.ref.close(false);
  }
}
